'use client';

import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { trackingLogService } from '@/features/tracking-log/trackingLogService';
import { trackEvent } from '@/shared/utils/mixpanel';
import logger from '@/utils/logger';
import axios from 'axios';
import https from 'https';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

interface ExchangeRateData {
  result: number;
  cur_unit: string;
  ttb: string;
  tts: string;
  deal_bas_r: string;
  bkpr: string;
  yy_efee_r: string;
  ten_dd_efee_r: string;
  kftc_bkpr: string;
  kftc_deal_bas_r: string;
}

interface ExchangeRate {
  USD: number;
  EUR: number;
  AUD: number;
  timestamp: string;
  yesterday: {
    USD: number;
    EUR: number;
    AUD: number;
  };
}

interface CostItem {
  id: string;
  name: string;
  price: number;
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

async function fetchExchangeRatesWithRetry(retries = 1, date?: string): Promise<ExchangeRateData[]> {
  const instance = axios.create({
    timeout: 10000, // 10초 타임아웃
    httpsAgent: new https.Agent({
      rejectUnauthorized: false, // ← 이게 verify=False 역할
    }),
  });

  try {
    const response = await instance.get(
      `${process.env.NEXT_PUBLIC_API_URL}/backoffice/exchange-rates${date ? `?date=${date}` : ''}`
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`Unexpected status: ${response.status}`);
    }

  } catch (error) {
    if (retries > 0) {
      logger.warn(`요청 실패, 1초 후 재시도합니다.. (${retries}회 남음)`, error);
      await delay(1000); // 🔁 1초 딜레이
      return await fetchExchangeRatesWithRetry(retries - 1, date);
    } else {
      logger.warn('요청 실패: 재시도 모두 실패', error);
      throw error;
    }
  }
}

export default function OfferCalculator() {
  const [productName, setProductName] = useState('');
  const [weight, setWeight] = useState({ kg: '', lb: '' });
  const [price, setPrice] = useState('');
  const [commission, setCommission] = useState('');
  const [buyCost, setBuyCost] = useState('');
  const [totalAmount, setTotalAmount] = useState('0');
  const [exchangeRatesValue, setExchangeRatesValue] = useState('');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRate | null>(null);
  const [weightUnit, setWeightUnit] = useState<'kg' | 'lb'>('kg');
  const [costItems, setCostItems] = useState<CostItem[]>([]);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  // Refs for input fields
  const productNameRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);
  const exchangeRatesRef = useRef<HTMLInputElement>(null);
  const commissionRef = useRef<HTMLInputElement>(null);
  const buyCostRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    trackingLogService.saveUtmParams();
    trackEvent('Page View', { 'Page Name': '오퍼가 계산기' });
  }, []);

  useEffect(() => {
    fetchExchangeRates();
    const interval = setInterval(fetchExchangeRates, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const fetchExchangeRates = async () => {
    try {
      // Check session storage first
      const cachedData = sessionStorage.getItem('exchangeRates');
      const cachedDataObject = JSON.parse(cachedData?.toString() || '{}');
      if ((cachedData && cachedDataObject.rates.yesterday.USD !== 0) || (cachedData && cachedDataObject.rates.yesterday.USD === null)) {
        const { rates, timestamp } = JSON.parse(cachedData);
        const cacheAge = Date.now() - new Date(timestamp).getTime();

        // Use cached data if it's less than 1 hour old
        if (cacheAge < 3600000) {
          setExchangeRates(rates);
          return;
        }
      }

      // 오늘자 환율 데이터 체크
      let apiData = await fetchExchangeRatesWithRetry(3) as ExchangeRateData[];
      const standardDate = new Date();
      let retryCount = 0;
      const maxRetries = 10;

      while (apiData.length === 0 && retryCount < maxRetries) {
        standardDate.setDate(standardDate.getDate() - 1);
        const formattedStandardDay = standardDate.toISOString().slice(0, 10).replace(/-/g, '');
        apiData = await fetchExchangeRatesWithRetry(3, formattedStandardDay) as ExchangeRateData[];
        retryCount++;
      }

      const yesterday = new Date(standardDate.setDate(standardDate.getDate() - 1));
      // Check if the date is a weekend or holiday and adjust accordingly
      const isWeekend = (date: Date) => {
        const day = date.getDay();
        return day === 0 || day === 6; // 0 is Sunday, 6 is Saturday
      };

      // Adjust date to get the last business day
      while (isWeekend(yesterday)) {
        yesterday.setDate(yesterday.getDate() - 1);
      }

      const formattedDate = yesterday
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, '');
      let yesterdayApiData = await fetchExchangeRatesWithRetry(3, formattedDate) as ExchangeRateData[];
      retryCount = 0;
      while (yesterdayApiData.length === 0 && retryCount < maxRetries) {
        yesterday.setDate(yesterday.getDate() - 1);
        const formattedYesterdayDate = yesterday.toISOString().slice(0, 10).replace(/-/g, '');
        yesterdayApiData = await fetchExchangeRatesWithRetry(3, formattedYesterdayDate) as ExchangeRateData[];
        retryCount++;
      }

      if (!apiData || apiData.length === 0 || !yesterdayApiData || yesterdayApiData.length === 0) {
        throw new Error('No exchange rate data available');
      }

      // API 응답 데이터를 원하는 형식으로 변환
      const rates: ExchangeRate = {
        USD: parseFloat(
          apiData
            .find((item) => item.cur_unit === 'USD')
            ?.deal_bas_r.replace(/,/g, '') || '1466.5',
        ),
        EUR: parseFloat(
          apiData
            .find((item) => item.cur_unit === 'EUR')
            ?.deal_bas_r.replace(/,/g, '') || '1587.85',
        ),
        AUD: parseFloat(
          apiData
            .find((item) => item.cur_unit === 'AUD')
            ?.deal_bas_r.replace(/,/g, '') || '920.67',
        ),
        timestamp: new Date().toISOString(),
        yesterday: {
          USD: parseFloat(
            yesterdayApiData
              .find((item) => item.cur_unit === 'USD')
              ?.deal_bas_r.replace(/,/g, '') || '1466.8',
          ),
          EUR: parseFloat(
            yesterdayApiData
              .find((item) => item.cur_unit === 'EUR')
              ?.deal_bas_r.replace(/,/g, '') || '1583.78',
          ),
          AUD: parseFloat(
            yesterdayApiData
              .find((item) => item.cur_unit === 'AUD')
              ?.deal_bas_r.replace(/,/g, '') || '923.79',
          ),
        },
      };

      // Save to session storage
      sessionStorage.setItem(
        'exchangeRates',
        JSON.stringify({
          rates,
          timestamp: standardDate.toISOString(),
        }),
      );

      setExchangeRates(rates);
    } catch (error) {
      logger.error('Failed to fetch exchange rates:', error);
      // 에러 발생 시 기본값 사용
    }
  };

  const calculateRateChange = (current: number, previous: number) => {
    const change = current - previous;
    const percentChange = (change / previous) * 100;
    return {
      change: Math.abs(change).toFixed(2),
      percent: Math.abs(percentChange).toFixed(2),
      isIncrease: change > 0,
    };
  };

  const handleWeightUnitChange = (value: 'kg' | 'lb') => {
    setWeightUnit(value);
    // Convert the current value to the new unit
    if (value === 'kg' && weight.lb) {
      const kgValue = (parseFloat(weight.lb) / 2.20462).toFixed(2);
      setWeight({ kg: kgValue, lb: weight.lb });
    } else if (value === 'lb' && weight.kg) {
      const lbValue = (parseFloat(weight.kg) * 2.20462).toFixed(2);
      setWeight({ kg: weight.kg, lb: lbValue });
    }

    calculateTotal(
      parseFloat(price.replace(/,/g, '')),
      value === 'kg' ? 1 : 2.204623,
      parseFloat(exchangeRatesValue.replace(/,/g, '')),
      parseFloat(commission.replace(/,/g, '')) / 100,
      parseFloat(buyCost.replace(/,/g, '')),
    );
  };

  const validateFields = () => {
    const newErrors: Record<string, boolean> = {};

    // 각 입력 필드의 현재 값을 직접 가져와서 검증
    const currentPrice = priceRef.current?.value || '0';
    const currentExchangeRate = exchangeRatesRef.current?.value || '0';
    const currentCommission = commissionRef.current?.value || '0';
    const currentBuyCost = buyCostRef.current?.value || '0';

    // Focus on the first empty field
    if (!currentPrice) {
      priceRef.current?.focus();
    } else if (!currentExchangeRate) {
      exchangeRatesRef.current?.focus();
    } else if (!currentCommission) {
      commissionRef.current?.focus();
    } else if (!currentBuyCost) {
      buyCostRef.current?.focus();
    }

    return Object.keys(newErrors).length === 0;
  };

  const calculateTotal = (
    offerPrice: number,
    offerStandardUnit: number,
    exchangeRate: number,
    taxRate: number,
    additionalCost: number,
  ) => {
    if (!validateFields()) return;
    let total = 0;

    // 총 예상원가 // 오퍼가 기준값 * 오퍼가 * 환율 + (오퍼가 기준값 * 오퍼가 * 환율 * 관세) + 부대비용
    // offerStandardUnit * offerPrice * exchangeRate + (offerStandardUnit * offerPrice * exchangeRate * taxRate) + additionalCost

    total =
      offerStandardUnit * offerPrice * exchangeRate + (offerStandardUnit * offerPrice * exchangeRate * taxRate) + additionalCost;

    setTotalAmount(addComma(Math.round(total).toString()));
  };

  // 새로운 항목 추가
  const addCostItem = () => {
    if (!price) return;

    const newItem: CostItem = {
      id: crypto.randomUUID(),
      name: productName,
      price: parseFloat(totalAmount.replace(/,/g, '')),
    };

    setCostItems((prev) => [...prev, newItem]);
  };

  // 개별 항목 삭제
  const removeCostItem = (id: string) => {
    setCostItems((prev) => prev.filter((item) => item.id !== id));
  };

  // 전체 항목 삭제
  const removeAllCostItems = () => {
    setCostItems([]);
  };

  // 저장하기 버튼 클릭 시
  const handleSave = () => {
    if (!validateFields()) return;
    addCostItem();
    trackEvent('Button Click', { 'Page Name': '오퍼가 계산기', 'Button Name': '저장하기' });
  };

  // 초기화 버튼 클릭 시
  const handleReset = () => {
    setProductName('');
    setPrice('');
    setCommission('');
    setBuyCost('');
    setExchangeRatesValue('');
    setTotalAmount('0');
    trackEvent('Button Click', { 'Page Name': '오퍼가 계산기', 'Button Name': '초기화' });
  };

  const addComma = (number: string) => {
    if (number !== '') {
      // 소수점을 포함한 숫자만 남기기
      number = number.replace(/[^0-9.]/g, '');

      // 소수점이 여러 개 있는 경우 첫 번째 소수점만 유지
      const decimalParts = number.split('.');
      if (decimalParts.length > 2) {
        number = decimalParts[0] + '.' + decimalParts.slice(1).join('');
      }

      // 소수점 2자리로 제한
      if (number.includes('.')) {
        const parts = number.split('.');
        if (parts[1].length > 2) {
          number = parts[0] + '.' + parts[1].slice(0, 2);
        }
      }

      // 천 단위마다 콤마 추가
      const formattedParts = number.split('.');
      formattedParts[0] = formattedParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      number = formattedParts.join('.');
    }
    return number;
  };

  // Clear errors when input changes
  const handleInputChange =
    (setter: (value: string) => void, field: string) =>
      (e: ChangeEvent<HTMLInputElement>) => {
        if (field === 'productName') {
          setter(e.target.value);
        } else {
          setter(addComma(e.target.value));
        }

        setErrors((prev) => ({ ...prev, [field]: false }));

        // 다음 tick에서 모든 필수 값이 있는지 확인하고 계산
        const updatedValue = e.target.value;
        const fieldValues = {
          price: field === 'price' ? updatedValue : price,
          exchangeRates:
            field === 'exchangeRates' ? updatedValue : exchangeRatesValue,
          commission: field === 'commission' ? updatedValue : commission,
          buyCost: field === 'buyCost' ? updatedValue : buyCost,
        };

        // 숫자 필드에 값이 없는 경우 0으로 설정
        fieldValues.price = fieldValues.price === '' ? '0' : fieldValues.price;
        fieldValues.exchangeRates =
          fieldValues.exchangeRates === '' ? '0' : fieldValues.exchangeRates;
        fieldValues.commission =
          fieldValues.commission === '' ? '0' : fieldValues.commission;
        fieldValues.buyCost =
          fieldValues.buyCost === '' ? '0' : fieldValues.buyCost;

        // 오퍼가 기준
        const offerStandardUnit = weightUnit === 'kg' ? 1 : 2.204623;
        // 오퍼가
        const offerPrice = parseFloat(fieldValues.price.replace(/,/g, ''));
        // 환율
        const exchangeRate = parseFloat(fieldValues.exchangeRates.replace(/,/g, ''));
        // 관세율
        const taxRate = parseFloat(fieldValues.commission.replace(/,/g, '')) / 100;
        // 부대비용
        const additionalCost = parseFloat(fieldValues.buyCost.replace(/,/g, ''));
        calculateTotal(
          offerPrice,
          offerStandardUnit,
          exchangeRate,
          taxRate,
          additionalCost,
        );
      };

  return (
    <div className="bg-white rounded-lg p-2 sm:p-4 md:p-8 w-full max-w-full overflow-x-hidden">
      <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-5 md:mb-6">오퍼가 계산기</h2>

      <div className="flex flex-col lg:flex-row gap-4 sm:gap-5 md:gap-6 w-full max-w-full overflow-x-hidden">
        <div className="w-full lg:w-4/6 border border-[#DEE2E7] rounded-lg overflow-hidden">
          <div className="flex flex-col h-full lg:flex-row min-h-fit">
            {/* 왼쪽: 입력 폼 */}
            <div className="w-full lg:w-3/5 lg:border-r border-gray-200 border-b lg:border-b-0 min-h-fit">
              <div className="p-3 sm:p-5 md:pl-6 md:pt-6 md:pr-8 flex flex-col">
                <div className="space-y-4 sm:space-y-5 md:space-y-[20px] flex-1">
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                    <label className="w-full md:w-1/3 md:min-w-[120px] md:h-12 md:flex md:items-center text-sm sm:text-base font-medium text-[#4A515D]">
                      품목정보
                    </label>
                    <div className="w-full md:w-2/3">
                      <Input
                        ref={productNameRef}
                        type="text"
                        className={`h-11 sm:h-12 text-sm sm:text-base w-full ${errors.productName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="품목명, 브랜드, 등급을 입력해주세요."
                        value={productName}
                        onChange={handleInputChange(
                          setProductName,
                          'productName',
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                    <label className="w-full md:w-1/3 md:min-w-[120px] md:h-12 md:flex md:items-center text-sm sm:text-base font-medium text-[#4A515D]">
                      오퍼가 기준
                    </label>
                    <div className="w-full md:w-2/3">
                      <div className="flex rounded-md overflow-hidden border border-[#DEE2E7] w-full h-11 sm:h-12">
                        <Button
                          onClick={() => handleWeightUnitChange('kg')}
                          className={`flex-1 px-2 sm:px-4 py-2 text-sm sm:text-base min-h-[44px] transition-colors border-transparent rounded-r-none ${weightUnit === 'kg'
                            ? 'bg-red-50 text-red-600 font-medium'
                            : 'bg-white hover:bg-gray-50 text-[#565C66]'
                            }`}
                        >
                          킬로그램(kg)
                        </Button>
                        <div className="w-[1px] bg-[#DEE2E7]" />
                        <Button
                          onClick={() => handleWeightUnitChange('lb')}
                          className={`flex-1 px-2 sm:px-4 py-2 text-sm sm:text-base min-h-[44px] transition-colors border-transparent rounded-l-none ${weightUnit === 'lb'
                            ? 'bg-red-50 text-red-600 font-medium'
                            : 'bg-white hover:bg-gray-50 text-[#565C66]'
                            }`}
                        >
                          파운드(lb)
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                    <label className="w-full md:w-1/3 md:min-w-[120px] md:h-12 md:flex md:items-center text-sm sm:text-base font-medium text-[#4A515D]">
                      현지 오퍼가
                    </label>
                    <div className="w-full md:w-2/3">
                      <div className="relative w-full">
                        <Input
                          ref={priceRef}
                          type="text"
                          placeholder="오퍼가를 입력해주세요"
                          value={price}
                          onChange={handleInputChange(setPrice, 'price')}
                          className={`pr-8 h-11 sm:h-12 text-sm sm:text-base w-full ${errors.price ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                    <label className="w-full md:w-1/3 md:min-w-[120px] md:h-12 md:flex md:items-center text-sm sm:text-base font-medium text-[#4A515D]">
                      환율
                    </label>
                    <div className="w-full md:w-2/3">
                      <div className="relative w-full">
                        <Input
                          ref={exchangeRatesRef}
                          type="text"
                          placeholder="환율을 입력해주세요"
                          value={exchangeRatesValue}
                          onChange={handleInputChange(
                            setExchangeRatesValue,
                            'exchangeRates',
                          )}
                          className={`pr-8 h-11 sm:h-12 text-sm sm:text-base w-full ${errors.exchangeRates ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs sm:text-sm text-[#93A0B4]">
                          원
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                    <label className="w-full md:w-1/3 md:min-w-[120px] md:h-12 md:flex md:items-center text-sm sm:text-base font-medium text-[#4A515D]">
                      관세율
                    </label>
                    <div className="w-full md:w-2/3">
                      <div className="relative w-full">
                        <Input
                          ref={commissionRef}
                          type="text"
                          placeholder="관세율을 입력해주세요"
                          value={commission}
                          onChange={handleInputChange(
                            setCommission,
                            'commission',
                          )}
                          className={`pr-8 h-11 sm:h-12 text-sm sm:text-base w-full ${errors.commission ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs sm:text-sm text-[#93A0B4]">
                          %
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                    <label className="w-full md:w-1/3 md:min-w-[120px] md:h-12 md:flex md:items-center text-sm sm:text-base font-medium text-[#4A515D]">
                      부대비용
                    </label>
                    <div className="w-full md:w-2/3">
                      <div className="relative w-full">
                        <Input
                          ref={buyCostRef}
                          type="text"
                          placeholder="부대비용을 입력해주세요"
                          value={buyCost}
                          onChange={handleInputChange(setBuyCost, 'buyCost')}
                          className={`pr-8 h-11 sm:h-12 text-sm sm:text-base w-full ${errors.buyCost ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs sm:text-sm text-[#93A0B4]">
                          원
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                    <label className="w-full md:w-1/3 md:min-w-[120px] md:h-12 md:flex md:items-center text-sm sm:text-base font-medium text-[#4A515D]">
                      예상원가
                    </label>
                    <div className="w-full md:w-2/3">
                      <div className="relative w-full">
                        <Input
                          type="text"
                          placeholder="정보를 입력하면 예상 원가가 표시 됩니다."
                          value={totalAmount}
                          readOnly
                          className={`pr-8 h-11 sm:h-12 font-bold text-red-600 text-base sm:text-lg md:text-xl w-full`}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs sm:text-sm text-[#93A0B4]">
                          원
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 sm:mt-8 md:mt-[52px]">
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto sm:min-w-[80px] h-11 sm:h-12 text-sm sm:text-base min-h-[44px]"
                      onClick={handleReset}
                    >
                      초기화
                    </Button>
                    <Button
                      className="w-full sm:flex-1 bg-slate-600 hover:bg-slate-700 h-11 sm:h-12 text-sm sm:text-base min-h-[44px]"
                      onClick={handleSave}
                    >
                      저장하기
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* 중앙: 예상원가 리스트 */}
            <div className="w-full lg:w-2/5 bg-white p-3 sm:p-5 md:p-6 relative flex flex-col">
              <div className="flex-1">
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">예상원가 리스트</h3>
                <div className="space-y-2 sm:space-y-3">
                  {costItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between bg-white rounded-lg p-3 sm:p-4 border text-[#5C6D85] text-sm sm:text-base"
                    >
                      <span className="truncate mr-2">{item.name}</span>
                      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                        <span className="text-xs sm:text-sm">{Math.round(item.price).toLocaleString()}원</span>
                        <button
                          className="text-gray-400 hover:text-gray-600 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                          onClick={() => removeCostItem(item.id)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button
                className="w-full h-11 sm:h-12 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 mt-3 sm:mt-4 text-sm sm:text-base min-h-[44px]"
                onClick={removeAllCostItems}
              >
                전체 삭제
              </button>
            </div>
          </div>
        </div>

        {/* 오른쪽: 환율 정보 */}
        <div className="w-full lg:w-2/6 space-y-3 sm:space-y-4 md:space-y-[20px] lg:h-full lg:flex lg:flex-col">
          {exchangeRates && (
            <>
              <div className="border border-[#DEE2E7] rounded-lg p-4 sm:p-5 md:pt-[50px] md:pl-[24px] md:pb-[30px] md:pr-[24px] lg:flex-1 lg:flex lg:flex-col lg:justify-center">
                <div className="flex justify-between items-center text-base sm:text-xl md:text-[24px]/[140%] text-[#5C6D85]">
                  <span>미국 USD</span>
                </div>
                <div className="flex justify-between items-baseline mt-4 sm:mt-6 md:mt-[32px]">
                  <div>
                    <span className="text-xl sm:text-2xl md:text-[24px]/[130%] lg:text-[32px]/[130%] font-bold">
                      {exchangeRates.USD}
                    </span>
                    <span className="text-sm sm:text-base ml-1">원</span>
                  </div>
                  {exchangeRates.yesterday && (
                    <div className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2 break-all leading-tight">
                      {(() => {
                        const change = calculateRateChange(
                          exchangeRates.USD,
                          exchangeRates.yesterday.USD,
                        );
                        return (
                          <>
                            <span
                              className={
                                change.isIncrease
                                  ? 'text-red-500'
                                  : 'text-blue-500'
                              }
                            >
                              {change.isIncrease ? '▲' : '▼'} {change.change}
                            </span>
                            <span
                              className={`ml-2 ${change.isIncrease ? 'text-red-500' : 'text-blue-500'}`}
                            >
                              {change.isIncrease ? '+' : '-'}
                              {change.percent}%
                            </span>
                            <span className="ml-2">
                              {new Date(
                                exchangeRates.timestamp,
                              ).toLocaleDateString('ko-KR')}{' '}
                              기준
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              <div className="border border-[#DEE2E7] rounded-lg p-4 sm:p-5 md:pt-[50px] md:pl-[24px] md:pb-[30px] md:pr-[24px] lg:flex-1 lg:flex lg:flex-col lg:justify-center">
                <div className="flex justify-between items-center text-base sm:text-xl md:text-[24px]/[140%] text-[#5C6D85]">
                  <span>유럽연합 EUR</span>
                </div>
                <div className="flex justify-between items-baseline mt-4 sm:mt-6 md:mt-[32px]">
                  <div>
                    <span className="text-xl sm:text-2xl md:text-[24px]/[130%] lg:text-[32px]/[130%] font-bold">
                      {exchangeRates.EUR}
                    </span>
                    <span className="text-sm sm:text-base ml-1">원</span>
                  </div>
                  {exchangeRates.yesterday && (
                    <div className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2 break-all leading-tight">
                      {(() => {
                        const change = calculateRateChange(
                          exchangeRates.EUR,
                          exchangeRates.yesterday.EUR,
                        );
                        return (
                          <>
                            <span
                              className={
                                change.isIncrease
                                  ? 'text-red-500'
                                  : 'text-blue-500'
                              }
                            >
                              {change.isIncrease ? '▲' : '▼'} {change.change}
                            </span>
                            <span
                              className={`ml-2 ${change.isIncrease ? 'text-red-500' : 'text-blue-500'}`}
                            >
                              {change.isIncrease ? '+' : '-'}
                              {change.percent}%
                            </span>
                            <span className="ml-2">
                              {new Date(
                                exchangeRates.timestamp,
                              ).toLocaleDateString('ko-KR')}{' '}
                              기준
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              <div className="border border-[#DEE2E7] rounded-lg p-4 sm:p-5 md:pt-[50px] md:pl-[24px] md:pb-[30px] md:pr-[24px] lg:flex-1 lg:flex lg:flex-col lg:justify-center">
                <div className="flex justify-between items-center text-base sm:text-xl md:text-[24px]/[140%] text-[#5C6D85]">
                  <span>호주 AUD</span>
                </div>
                <div className="flex justify-between items-baseline mt-4 sm:mt-6 md:mt-[32px]">
                  <div>
                    <span className="text-xl sm:text-2xl md:text-[24px]/[130%] lg:text-[32px]/[130%] font-bold">
                      {exchangeRates.AUD}
                    </span>
                    <span className="text-sm sm:text-base ml-1">원</span>
                  </div>
                  {exchangeRates.yesterday && (
                    <div className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2 break-all leading-tight">
                      {(() => {
                        const change = calculateRateChange(
                          exchangeRates.AUD,
                          exchangeRates.yesterday.AUD,
                        );
                        return (
                          <>
                            <span
                              className={
                                change.isIncrease
                                  ? 'text-red-500'
                                  : 'text-blue-500'
                              }
                            >
                              {change.isIncrease ? '▲' : '▼'} {change.change}
                            </span>
                            <span
                              className={`ml-2 ${change.isIncrease ? 'text-red-500' : 'text-blue-500'}`}
                            >
                              {change.isIncrease ? '+' : '-'}
                              {change.percent}%
                            </span>
                            <span className="ml-2">
                              {new Date(
                                exchangeRates.timestamp,
                              ).toLocaleDateString('ko-KR')}{' '}
                              기준
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-5 mt-8 sm:mt-10 md:mt-[72px]">관세율 표</h2>

      {/* 관세율 표 */}
      <div className="space-y-8">
        {/* 우육 (냉장/냉동) */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[900px]">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-3 py-2 font-semibold text-center" rowSpan={2}>축종</th>
                  <th className="border border-gray-200 px-3 py-2 font-semibold text-center" rowSpan={2}>분류</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={5} rowSpan={2}>부위명</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>미국</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>호주</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>뉴질랜드</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>캐나다</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>EU</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold">멕시코</th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026.7~</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027.7~</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                </tr>
              </thead>
              <tbody>
                {/* 정육, 갈비 외 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 font-bold text-xl text-center" rowSpan={16}>우육<br /><span className="text-sm font-normal text-gray-500">(냉장/냉동)</span></td>
                  <td className="border border-gray-200 px-3 py-2 text-center" rowSpan={9}>정육, 갈비 외</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">갈비</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">척갈비</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={9}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={9}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={9}>5.3%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={9}>2.6%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={9}>8.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={9}>5.3%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={9}>8.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={9}>5.3%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={9}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={9}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={9}>40.0%</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">등갈비</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">바베큐립</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">스페어립</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">양지갈비</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">조각갈비</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">등심</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">안심</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">채끝</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">늑간살</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">양지늑간</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">조각늑간</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">진갈비살</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">갈비본살</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">목심</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">전각</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">부채</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">살치</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">차돌양지</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">삼겹양지</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">차돌박이</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">사태</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">우둔</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">홍두깨</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">설깃</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">업진살</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">제비추리</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">뼈사태</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">사골</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">반골</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">목뼈</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">학본</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
                {/* 특수부위 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center" rowSpan={5}>특수부위</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">안창</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">토시</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">볼살</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">스지</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={5}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={5}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={5}>2.4%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={5}>1.2%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={5}>3.6%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={5}>2.4%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={5}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={5}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={5}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={5}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={5}>18.0%</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">도가니</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">꼬리</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">우설</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">우족</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
                {/* 내장 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center" rowSpan={2}>내장</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">곱창</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">대창</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">홍창</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={2}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={2}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={2}>3.6%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={2}>1.8%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={2}>5.4%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={2}>3.6%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={2}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={2}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={2}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={2}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={2}>27.0%</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">벌집위</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">천엽</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">양깃머리</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 돈육 (냉동) */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[900px]">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-3 py-2 font-semibold text-center" rowSpan={2}>축종</th>
                  <th className="border border-gray-200 px-3 py-2 font-semibold text-center" rowSpan={2}>분류2_(내부분류)</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={5} rowSpan={2}>부위명</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>EU</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>미국</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>캐나다</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>호주</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold">멕시코</th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026.7~</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027.7~</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                </tr>
              </thead>
              <tbody>
                {/* 삼겹류 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 font-bold text-xl text-center" rowSpan={10}>돈육<br /><span className="text-sm font-normal text-gray-500">(냉동)</span></td>
                  <td className="border border-gray-200 px-3 py-2 text-center">삼겹류</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">삼겹</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">뼈삼겹</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">미박삼겹</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">뼈미박삼겹</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">1.9%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">25.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">25.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">25.0%</td>
                </tr>
                {/* 정육, 갈비 외 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center" rowSpan={4}>정육, 갈비 외</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">목살</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">목전지</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">전지</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">후지</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">등심</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={4}>25.0%</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">돈갈비</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">스페어립</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">목뼈</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">등뼈</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">항정</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">가브리</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">사골</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">장족</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">학</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">볼살</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">머릿고기</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">돼지머리</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">관자놀이</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">염통</td>
                </tr>
                {/* 특수부위 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center">특수부위</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">갈매기</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">미니족</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">꼬리</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">18.0%</td>
                </tr>
                {/* 돼지장 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center">돼지장</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">대창</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">막창</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">소창</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">27.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">27.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">27.0%</td>
                </tr>
                {/* 돼지위 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center">돼지위</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">돈위</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">3.6%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">1.8%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">27.0%</td>
                </tr>
                {/* 돼지방광 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center">돼지방광</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">방광</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">27.0%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 돈육 (냉장) */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[900px]">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-3 py-2 font-semibold text-center" rowSpan={2}>축종</th>
                  <th className="border border-gray-200 px-3 py-2 font-semibold text-center" rowSpan={2}>분류2_(내부분류)</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={5} rowSpan={2}>부위명</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>EU</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>미국</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>캐나다</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>호주</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold">멕시코</th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026.7~</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027.7~</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                </tr>
              </thead>
              <tbody>
                {/* 삼겹류 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 font-bold text-xl text-center" rowSpan={10}>돈육<br /><span className="text-sm font-normal text-gray-500">(냉장)</span></td>
                  <td className="border border-gray-200 px-3 py-2 text-center">삼겹류</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">삼겹</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">뼈삼겹</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">미박삼겹</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">뼈미박삼겹</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">1.7%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">22.5%</td>
                </tr>
                {/* 정육, 갈비 외 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center" rowSpan={4}>정육, 갈비 외</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">목살</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">목전지</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">전지</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">후지</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">등심</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" rowSpan={4}>1.7%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={4}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={4}>3.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={4}>1.5%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={4}>22.5%</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">돈갈비</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">스페어립</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">목뼈</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">등뼈</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">항정</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">가브리</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">사골</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">장족</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">학</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">볼살</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">머릿고기</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">돼지머리</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">관자놀이</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">염통</td>
                </tr>
                {/* 특수부위 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center">특수부위</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">갈매기</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">미니족</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">꼬리</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">18.0%</td>
                </tr>
                {/* 돼지장 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center">돼지장</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">대창</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">막창</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">소창</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">27.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">27.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">27.0%</td>
                </tr>
                {/* 돼지위 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center">돼지위</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">돈위</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">3.6%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">1.8%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">27.0%</td>
                </tr>
                {/* 돼지방광 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center">돼지방광</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">방광</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">27.0%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 양/염소 */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[700px]">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-3 py-2 font-semibold text-center" rowSpan={2}>축종</th>
                  <th className="border border-gray-200 px-3 py-2 font-semibold text-center" rowSpan={2}>분류2_(내부분류)</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={5} rowSpan={2}>부위명</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>호주</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>뉴질랜드</th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                </tr>
              </thead>
              <tbody>
                {/* 정육, 갈비 외 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 font-bold text-xl text-center" rowSpan={4}>양<br />염소</td>
                  <td className="border border-gray-200 px-3 py-2 text-center" rowSpan={3}>정육, 갈비 외</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">프렌치랙</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">숄더랙</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">사각갈비</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">뼈사태</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={3}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={3}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={3}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={3}>0.0%</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">양정육</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">앞다리</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">양삼겹</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">뒷다리</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">잡육</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-2 py-1 text-center">척롤</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">플랩</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">등심</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">어깨</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
                {/* 특수부위 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center">특수부위</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">간</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">염통</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">깐양</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">꼬리</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">2.4%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">1.2%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">6.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">5.0%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 닭 */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[700px]">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-3 py-2 font-semibold text-center" rowSpan={2}>축종</th>
                  <th className="border border-gray-200 px-3 py-2 font-semibold text-center" rowSpan={2}>분류2_(내부분류)</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={4} rowSpan={2}>부위명</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>브라질</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>태국</th>
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold" colSpan={2}>미국</th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2026</th>
                  <th className="border border-gray-200 px-2 py-1 text-center text-xs">2027</th>
                </tr>
              </thead>
              <tbody>
                {/* 정육, 갈비 외 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 font-bold text-xl text-center" rowSpan={3}>닭</td>
                  <td className="border border-gray-200 px-3 py-2 text-center" rowSpan={2}>정육, 갈비 외</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">닭봉</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">북채</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">20.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">20.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">20.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black">20.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40">0.0%</td>
                </tr>
                <tr>

                  <td className="border border-gray-200 px-2 py-1 text-center">정육</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">목살</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">가슴살</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">장각</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={2}>27.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={2}>27.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={2}>5.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center font-bold text-black" rowSpan={2}>5.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={2}>0.0%</td>
                  <td className="border border-gray-200 px-2 py-1 text-center opacity-40" rowSpan={2}>0.0%</td>
                </tr>
                {/* 특수부위 */}
                <tr>
                  <td className="border border-gray-200 px-3 py-2 text-center">특수부위</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">닭발</td>
                  <td className="border border-gray-200 px-2 py-1 text-center">염통</td>
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                  <td className="border border-gray-200 px-2 py-1 text-center" />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
